import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface MatrixRow {
  id: string;
  text: string;
}

interface MatrixRowSelectorProps {
  // Pregunta seleccionada
  questionName: string;
  questionId?: string;

  // ID del formulario
  jotformFormId: string;

  // Valores seleccionados
  selectedRowId?: string;

  // Callbacks
  onRowChange: (rowId: string, rowText: string) => void;

  // Configuración
  className?: string;
}

export function MatrixRowSelector({
  questionName,
  questionId,
  jotformFormId,
  selectedRowId,
  onRowChange,
  className
}: MatrixRowSelectorProps) {
  const [matrixRows, setMatrixRows] = useState<MatrixRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar estructura de la matriz desde JotForm
  useEffect(() => {
    const fetchMatrixData = async () => {
      if (!questionName || !jotformFormId) {
        setMatrixRows([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const JOTFORM_API_KEY = 'ab0570e246def5ab17e4a6ad141fe48d';
        const JOTFORM_API_URL = 'https://eu-api.jotform.com';

        console.log('🔍 [MatrixRowSelector] Cargando datos de matriz para pregunta:', questionName);

        // Obtener la estructura del formulario para extraer la información de la pregunta
        const response = await fetch(
          `${JOTFORM_API_URL}/form/${jotformFormId}/questions?apiKey=${JOTFORM_API_KEY}`
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: No se pudieron obtener las preguntas del formulario.`);
        }

        const data = await response.json();
        const questions = data.content || {};

        console.log('🔍 [MatrixRowSelector] Preguntas obtenidas:', questions);

        // Buscar la pregunta específica por su name
        const questionData = Object.values(questions).find(
          (q: any) => q.name === questionName
        ) as any;

        if (!questionData) {
          throw new Error(`Pregunta "${questionName}" no encontrada en el formulario`);
        }

        console.log('🔍 [MatrixRowSelector] Datos de la pregunta:', questionData);

        // Verificar que sea de tipo matriz
        if (questionData.type !== 'control_matrix') {
          throw new Error(`La pregunta "${questionName}" no es de tipo matriz`);
        }

        // Extraer filas: primero desde drows, luego con fallbacks (rows en string/array)
        let rows: MatrixRow[] = [];
        if (questionData.drows) {
          try {
            const drows = JSON.parse(questionData.drows);
            rows = drows.map((row: any) => ({
              id: row.id ?? row.text ?? String(row),
              text: row.text ?? row.id ?? String(row)
            }));
          } catch (e) {
            console.warn('⚠️ [MatrixRowSelector] No se pudo parsear drows, intentando fallbacks', e);
          }
        }

        if (rows.length === 0) {
          const rawRows = questionData.rows || questionData?.properties?.rows || questionData?.options;
          if (rawRows) {
            if (typeof rawRows === 'string') {
              rows = rawRows.split('|').map((r: string) => r.trim()).filter(Boolean).map((r: string) => ({ id: r, text: r }));
            } else if (Array.isArray(rawRows)) {
              rows = rawRows.map((r: any) => {
                if (typeof r === 'string') return { id: r, text: r };
                const txt = r.text ?? r.label ?? r.value ?? r.id ?? '';
                const id = r.id ?? r.value ?? txt;
                return { id: String(id), text: String(txt) };
              }).filter((r: MatrixRow) => r.text);
            }
          }
        }

        console.log('✅ [MatrixRowSelector] Filas de matriz:', rows);
        setMatrixRows(rows);

      } catch (error) {
        console.error('❌ [MatrixRowSelector] Error cargando datos de matriz:', error);
        setError(error instanceof Error ? error.message : 'Error desconocido');
        setMatrixRows([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatrixData();
  }, [questionName, jotformFormId]);

  // Si no hay pregunta, no mostrar nada
  if (!questionName) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="matrix-row" className="text-sm font-medium">
        Fila de la Matriz
      </Label>
      {isLoading ? (
        <div className="text-sm text-muted-foreground py-2">
          Cargando estructura de la matriz...
        </div>
      ) : error ? (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : matrixRows.length > 0 ? (
        <Select
          value={selectedRowId || ''}
          onValueChange={(value) => {
            const row = matrixRows.find(r => r.id === value);
            if (row) {
              onRowChange(row.id, row.text);
            }
          }}
        >
          <SelectTrigger id="matrix-row">
            <SelectValue placeholder="Selecciona una fila" />
          </SelectTrigger>
          <SelectContent>
            {matrixRows.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-sm">
            No se encontraron filas configuradas para esta pregunta de matriz.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
